import { useSyncExternalStore } from 'react';

/**
 * «La pagina e' nascosta?», in una forma sola per tutta l'app.
 *
 * Serviva perche' la stessa domanda veniva fatta in due modi diversi e
 * incompatibili: `useVisibility` la osserva come EVENTO (sei appena passato in
 * secondo piano), mentre qui interessa lo STATO (sei in secondo piano adesso).
 * Sono due cose diverse, e la seconda mancava del tutto: un componente montato
 * a pagina gia' nascosta, o un ramo dello store che gira fuori da React, non
 * avevano modo di saperlo.
 *
 * Due forme, una verita':
 *  - `paginaNascosta()` — imperativa, per lo store Zustand, che sta fuori dai
 *    componenti e non puo' usare un hook;
 *  - `usePaginaNascosta()` — reattiva, per i componenti.
 *
 * ⚠️ `pagehide` scrive `true` **senza** rileggere `visibilityState`, e non e'
 * una svista. Su iOS il passaggio ad un'altra app puo' congelare la pagina
 * senza emettere `visibilitychange`: in quel momento `visibilityState` e'
 * ancora `'visible'`, e una guardia che lo rileggesse annullerebbe proprio il
 * ripiego che copre quel caso. E' il difetto per cui il ripiego di
 * `useArenaQueueGuard` non funzionava: c'era, ma si autoannullava.
 *
 * Gli ascoltatori si registrano una volta sola a livello di modulo — la
 * domanda e' globale, e ogni componente che la fa non deve aggiungerne di
 * propri.
 */

function leggiDalDocumento(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

let nascosta = leggiDalDocumento();
const iscritti = new Set<() => void>();

function scrivi(valore: boolean): void {
  if (nascosta === valore) return;
  nascosta = valore;
  for (const avvisa of iscritti) avvisa();
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => scrivi(leggiDalDocumento()));
  /* Vedi sopra: qui non si rilegge `visibilityState`. */
  window.addEventListener('pagehide', () => scrivi(true));
  window.addEventListener('pageshow', () => scrivi(leggiDalDocumento()));
}

/** Lo stato adesso, per chi sta fuori da React. */
export function paginaNascosta(): boolean {
  return nascosta;
}

function iscriviti(avvisa: () => void): () => void {
  iscritti.add(avvisa);
  return () => {
    iscritti.delete(avvisa);
  };
}

/**
 * Lo stato adesso, reattivo.
 *
 * Il valore iniziale si legge dal documento e non da un `false` di comodo: un
 * componente che si monta a pagina gia' nascosta — l'overlay di fine partita
 * che compare mentre il telefono e' in tasca — deve saperlo dal primo render,
 * non al primo evento successivo.
 */
export function usePaginaNascosta(): boolean {
  return useSyncExternalStore(iscriviti, paginaNascosta, () => false);
}
